import React, { useId, useRef, useState } from "react";
import { useOrganization } from "@clerk/react";
import { useEntitlements } from "../../hooks/useEntitlements";

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

export function InviteMemberModal({ onClose, onSuccess }: Props) {
  const emailId = useId();
  const roleId = useId();

  const { organization } = useOrganization();

  // Per-seat add cost — pure display, no API call. Mirrors the live tier:
  // Security ($50/seat) implies Base, otherwise Base ($20/seat). Billing
  // reconciles automatically via the Clerk → LS seat-sync webhook (Spec 7 §9).
  const ent = useEntitlements();
  const perSeat =
    ent.kind === "ready" && ent.hasSecurity
      ? "$100"
      : ent.kind === "ready" && ent.hasPro
        ? "$50"
        : "$20";

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const firstInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  function validate(): boolean {
    if (!email.trim() || !email.includes("@")) {
      setFieldError("Please enter a valid email address.");
      return false;
    }
    setFieldError(null);
    return true;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    if (!organization) {
      setApiError("No active organization.");
      return;
    }

    setSubmitting(true);
    setApiError(null);
    try {
      // Clerk wants role as "org:admin" / "org:member". UI uses bare strings.
      await organization.inviteMember({
        emailAddress: email.trim(),
        role: `org:${role}`,
      });
      onSuccess();
    } catch (err) {
      // Clerk SDK errors carry the user-facing message in .message; fall back
      // to a generic copy when the throw isn't an Error (shouldn't happen, but
      // mirrors the legacy API client's defensive pattern).
      if (err instanceof Error) {
        setApiError(err.message);
      } else {
        setApiError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="sr-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="sr-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-modal-title"
      >
        <div className="sr-modal__header">
          <h3 id="invite-modal-title" className="sr-modal__title">
            Invite member
          </h3>
          <button type="button" className="sr-modal__close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <form className="sr-modal__body" onSubmit={handleSubmit} noValidate>
          {apiError !== null && (
            <div className="sr-modal__api-error" role="alert">
              {apiError}
            </div>
          )}

          <div className="sr-modal__field">
            <label htmlFor={emailId} className="sr-modal__label">
              Email address
            </label>
            <input
              ref={firstInputRef}
              id={emailId}
              type="email"
              className="sr-modal__input"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (fieldError) setFieldError(null);
              }}
              placeholder="teammate@example.com"
              autoComplete="email"
              disabled={submitting}
            />
            {fieldError !== null && (
              <span className="sr-modal__field-error" role="alert">
                {fieldError}
              </span>
            )}
          </div>

          <div className="sr-modal__field">
            <label htmlFor={roleId} className="sr-modal__label">
              Role
            </label>
            <select
              id={roleId}
              className="sr-modal__select"
              value={role}
              onChange={(e) => setRole(e.target.value as "admin" | "member")}
              disabled={submitting}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Only render once entitlements resolve to a known paid tier.
              While loading, perSeat would default to "$20" and a Security-tier
              org would briefly flash "+$20/mo" before correcting to "+$50/mo";
              for a no-plan org the price is meaningless. Gating on "ready"
              shows a confirmed price or nothing. */}
          {ent.kind === "ready" && (
            <p className="ac-seat-note">
              Adding a member adds 1 seat ≈ +{perSeat}/mo (prorated). Billing updates automatically.
            </p>
          )}

          <div className="sr-modal__footer">
            <button
              type="button"
              className="sr-btn sr-btn--ghost"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="sr-btn"
              disabled={submitting || !organization}
              aria-busy={submitting}
            >
              {submitting ? "Sending…" : "Send invite"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
